/**
 * BootstrapBrowser — the component library, Bootstrap-Docs style.
 *
 * Reads `registry.json` (via `/api/widget-native/registry`) and renders one
 * card per entry, grouped L0 → L4.
 *
 * The entries are TWO kinds, because the registry stores them differently:
 * `components[]` holds L1–L4, while L0 Foundations lives in the `tokens`
 * object as GROUPS of CSS variable names. Both are folded into one universe up
 * front so filtering, searching and every count treat them alike — otherwise
 * L0 shows "0 cards", which reads as "the design system has no foundations".
 *
 * The two filter rows COMPOSE (layer ∧ family ∧ search) and every count is
 * computed against the OTHER filters, never the whole registry: pick a layer
 * and the family counts collapse to what that layer actually contains. A
 * family with nothing left is disabled rather than hidden, so the row doesn't
 * jump around under the cursor.
 *
 * It also shows the DRIFT between the registry and this runtime's kit: a
 * component marked `render:"implemented"` with no web implementation is a bug
 * you want to see here, not on a blank widget page.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ComponentCard from './ComponentCard';
import TokenGroupCard from './TokenGroupCard';
import { bootstrapHref, bootstrapAnchorId } from './studioNav';
import { missingImplementations, extraImplementations } from '../viewir/registry';
import { DERIVE_NAMES } from '../viewir/derives';

const C = { text: '#1c1c1e', dim: '#6e6e73', border: '#e2e2ea', card: '#fff', accent: '#6E59F2' };

const chip = (on, disabled) => ({
  padding: '5px 11px', borderRadius: 999, fontSize: 12, fontWeight: 600,
  cursor: disabled ? 'not-allowed' : 'pointer',
  border: `1px solid ${on ? C.accent : C.border}`,
  background: on ? C.accent : '#fff',
  color: on ? '#fff' : (disabled ? '#b8b8c0' : C.text),
  opacity: disabled ? 0.55 : 1,
  whiteSpace: 'nowrap',
});

const rowLabel = {
  fontSize: 10.5, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase',
  color: C.dim, marginRight: 2,
};

/** Everything a full-text search should match, flattened once per entry. */
function haystack(e) {
  if (e.kind === 'tokens') return [e.id, e.layer, e.family, ...e.names].join(' ').toLowerCase();
  return [
    e.id, e.name, e.description, e.family, e.layer,
    ...(e.variants || []), ...(e.states || []), ...(e.sealed || []), ...(e.overrides || []),
    ...(e.signals || []).map((s) => s.name), ...(e.slots || []).map((s) => s.name),
  ].join(' ').toLowerCase();
}

/**
 * `registry.tokens` → L0 entries, one per GROUP. `note` is prose, not a group.
 * Shaped like a component entry (id / layer / family) so a single filter
 * pipeline serves both kinds.
 */
function tokenEntries(registry) {
  const tokens = registry?.tokens || {};
  return Object.entries(tokens)
    .filter(([key, value]) => key !== 'note' && Array.isArray(value))
    .map(([group, names]) => ({ kind: 'tokens', id: group, layer: 'L0', family: 'tokens', names }));
}

export default function BootstrapBrowser({ registry, locate = null }) {
  const navigate = useNavigate();
  // `locate` is a deep link (`?component=<id>`) from elsewhere — a studio module's
  // type badge, or a chat card's component name. It does NOT filter the library:
  // it SCROLLS the whole page to that component's card and highlights it, exactly
  // like following a GitHub README `#section` anchor. Search stays independent.
  const [q, setQ] = useState('');
  const [layer, setLayer] = useState('all');
  const [family, setFamily] = useState('all');

  // Land on the located card: drop any active filters so it is guaranteed on the
  // page, then scroll it into view. `registry` in the deps means we re-run once it
  // has loaded and the cards are in the DOM. We RE-scroll a few times over ~1s
  // because the live component previews load async and keep shifting the layout
  // after the first paint — a single scroll lands, then the target moves under it.
  useEffect(() => {
    if (!locate) return;
    setLayer('all');
    setFamily('all');
    const id = bootstrapAnchorId(locate);
    let stopped = false;
    const timers = [];
    const scrollToIt = (smooth) => {
      const el = document.getElementById(id);
      // Optional-call, same as interpreter.jsx's scroll sink: jsdom (vitest) has no
      // scrollIntoView, and a missing scroll is a no-op, not a crash.
      el?.scrollIntoView?.({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
    };
    // t=0 jump instantly, then smooth re-corrections as previews settle.
    scrollToIt(false);
    [120, 300, 600, 1000].forEach((ms) => {
      timers.push(setTimeout(() => { if (!stopped) scrollToIt(true); }, ms));
    });
    return () => { stopped = true; timers.forEach(clearTimeout); };
  }, [locate, registry]);

  // A card's "lays out" link is the same deep link, so it travels through the
  // URL rather than local state — the address bar keeps pointing at what you
  // are looking at, and back returns to the card you jumped from.
  const jumpTo = useCallback((type) => navigate(bootstrapHref(type)), [navigate]);

  // ONE universe: L0 token groups + L1–L3 components + the L4 patterns.
  //
  // THREE kinds now, because the registry stores three. `components[]` is
  // exactly what a blueprint may NAME, so the L4 recipes moved out of it into
  // `patterns[]` — a pattern is prose about how to compose, not a thing to
  // name. They still belong on this page (the recommended starting point for a
  // new widget is a recipe, not a component), so they are folded in here and
  // draw the same recipe card they always did.
  const universe = useMemo(
    () => [
      ...tokenEntries(registry),
      ...(registry?.components || []).map((c) => ({ ...c, kind: 'component' })),
      ...(registry?.patterns || []).map((p) => ({
        ...p, kind: 'component', layer: p.layer || 'L4', family: p.family || 'pattern',
      })),
    ],
    [registry],
  );

  const families = useMemo(() => [...new Set(universe.map((e) => e.family))].sort(), [universe]);

  // Search first, so typing narrows the chips as well as the cards.
  const searched = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return universe;
    return universe.filter((e) => haystack(e).includes(needle));
  }, [universe, q]);

  const filtered = useMemo(
    () => searched.filter(
      (e) => (layer === 'all' || e.layer === layer) && (family === 'all' || e.family === family),
    ),
    [searched, layer, family],
  );

  // CROSS-FILTERED counts: a LAYER chip counts within the active family, a
  // FAMILY chip within the active layer. The two rows therefore always agree
  // with each other and with the header.
  const layerCount = (id) => searched.filter(
    (e) => (id === 'all' || e.layer === id) && (family === 'all' || e.family === family),
  ).length;
  const familyCount = (f) => searched.filter(
    (e) => (f === 'all' || e.family === f) && (layer === 'all' || e.layer === layer),
  ).length;

  const byLayer = useMemo(() => {
    const m = new Map();
    for (const e of filtered) {
      if (!m.has(e.layer)) m.set(e.layer, []);
      m.get(e.layer).push(e);
    }
    return m;
  }, [filtered]);

  const missing = missingImplementations(registry);
  const extra = extraImplementations(registry);
  const implemented = universe.filter((e) => e.kind === 'component' && e.render === 'implemented').length;
  const clearAll = () => { setQ(''); setLayer('all'); setFamily('all'); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search components, ports, sealed rules, token names…"
          style={{
            flex: '1 1 260px', minWidth: 200, padding: '8px 12px', fontSize: 13,
            border: `1px solid ${C.border}`, borderRadius: 8, outline: 'none', background: '#fff',
          }}
        />
        <span style={{ fontSize: 12, color: C.dim }}>
          {filtered.length} / {universe.length} entries · {implemented} implemented components · {DERIVE_NAMES.length} named derives
        </span>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={rowLabel}>Layer</span>
        <button type="button" onClick={() => setLayer('all')} style={chip(layer === 'all')}>
          All · {layerCount('all')}
        </button>
        {(registry?.layers || []).map((l) => {
          const n = layerCount(l.id);
          const off = n === 0 && layer !== l.id;
          return (
            <button
              key={l.id} type="button" title={l.blurb} disabled={off}
              onClick={() => setLayer(l.id)} style={chip(layer === l.id, off)}
            >
              {l.id} {l.name} · {n}
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={rowLabel}>Family</span>
        <button type="button" onClick={() => setFamily('all')} style={chip(family === 'all')}>
          All · {familyCount('all')}
        </button>
        {families.map((f) => {
          const n = familyCount(f);
          const off = n === 0 && family !== f;
          return (
            <button
              key={f} type="button" disabled={off}
              onClick={() => setFamily(f)} style={chip(family === f, off)}
            >
              {f} · {n}
            </button>
          );
        })}
      </div>

      {(missing.length || extra.length) ? (
        <div style={{
          fontSize: 12, borderRadius: 10, padding: 12, lineHeight: 1.5,
          background: missing.length ? '#fff1f0' : '#f6f6f9',
          border: `1px solid ${missing.length ? '#ffd0cc' : C.border}`,
          color: missing.length ? '#b3261e' : C.dim,
        }}>
          {missing.length ? <div><strong>Registry ahead of the web kit:</strong> {missing.join(', ')} marked implemented with no web component.</div> : null}
          {extra.length ? <div>Web kit ahead of the registry: {extra.join(', ')}.</div> : null}
        </div>
      ) : null}

      {[...byLayer.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([layerId, list]) => {
        const meta = (registry?.layers || []).find((l) => l.id === layerId);
        return (
          <section key={layerId} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>
                {layerId} · {meta?.name || ''}{' '}
                <span style={{ fontWeight: 400, color: C.dim, fontSize: 12 }}>
                  authoring: {meta?.authoring || '—'} · {list.length}
                </span>
              </h3>
              {meta?.blurb ? (
                <p style={{ margin: '4px 0 0', fontSize: 12.5, color: C.dim, maxWidth: 900, lineHeight: 1.5 }}>{meta.blurb}</p>
              ) : null}
            </div>
            <div style={{
              display: 'grid', gap: 16,
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            }}>
              {list.map((e) => (e.kind === 'tokens'
                ? <TokenGroupCard key={`t-${e.id}`} group={e.id} names={e.names} />
                : <ComponentCard key={e.id} comp={e} onJump={jumpTo} highlight={e.id === locate} />))}
            </div>
          </section>
        );
      })}

      {filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: C.dim, fontSize: 13, lineHeight: 1.6 }}>
          <div>
            Nothing matches{q ? ` “${q}”` : ''}
            {layer !== 'all' ? ` in ${layer}` : ''}
            {family !== 'all' ? ` · family “${family}”` : ''}.
          </div>
          <button type="button" onClick={clearAll} style={{ ...chip(false), marginTop: 12, padding: '6px 14px' }}>
            Clear filters
          </button>
        </div>
      ) : null}

      <section style={{
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16,
        display: 'flex', flexDirection: 'column', gap: 8,
      }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>Named derives (closed set)</h3>
        <p style={{ margin: 0, fontSize: 12.5, color: C.dim, lineHeight: 1.5, maxWidth: 860 }}>
          A blueprint’s <code>{'{ "op": "derive", "expr": … }'}</code> may only name one of these. Both runtimes
          implement the same eight identically — adding one is an app-release change, not a blueprint change.
        </p>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {DERIVE_NAMES.map((d) => (
            <code key={d} style={{
              fontSize: 11.5, padding: '3px 9px', borderRadius: 6,
              background: '#f4f4f7', border: `1px solid ${C.border}`,
            }}>{d}</code>
          ))}
        </div>
      </section>
    </div>
  );
}
